/**
 * Type-safe helpers for creating and parsing composite booking IDs
 * used for absences and events in the booking system.
 */

export interface ParsedAbsenceId {
  employeeId: string
  absenceId: string
}

export interface ParsedEventId {
  eventId: string
}

/**
 * Creates a composite booking ID for an absence
 * Format: absence-{employeeId}-{absenceId}
 */
export const createAbsenceBookingId = (employeeId: string, absenceId: string): string => {
  return `absence-${employeeId}-${absenceId}`
}

/**
 * Creates a composite booking ID for an event
 * Format: event-{eventId}
 */
export const createEventBookingId = (eventId: string): string => {
  return `event-${eventId}`
}

/**
 * Parses an absence booking ID and extracts employee and absence IDs
 * Returns null if the ID doesn't match the expected format
 */
export const parseAbsenceBookingId = (bookingId: string): ParsedAbsenceId | null => {
  const match = bookingId.match(/^absence-(.+)-(.+)$/)
  if (!match) return null
  
  return {
    employeeId: match[1],
    absenceId: match[2],
  }
}

/**
 * Parses an event booking ID and extracts the event ID
 * Returns null if the ID doesn't match the expected format
 */
export const parseEventBookingId = (bookingId: string): ParsedEventId | null => {
  const match = bookingId.match(/^event-(.+)$/)
  if (!match) return null
  
  return {
    eventId: match[1],
  }
}

/**
 * Checks if a booking ID is for an absence
 */
export const isAbsenceBookingId = (bookingId: string): boolean => {
  return bookingId.startsWith('absence-')
}

/**
 * Checks if a booking ID is for an event
 */
export const isEventBookingId = (bookingId: string): boolean => {
  return bookingId.startsWith('event-')
}
